import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { getCookie } from '../helpers/cookieHelpers';
import { logger } from '../helpers/logger';
import { useDataLayerSync } from '../hooks/useDataLayerSync';
import { useIsHydrated } from '../hooks/useIsHydrated';
import { fetchPage } from '../store/actions/page';
import Page from './Page';

// TODO: Move cookie name to config file
const PartnerCookieName = 'partner';

/**
 * Handles dispatching page data loading actions and rendering the page content.
 * @param {any} props
 */
function PageDataLoader() {
  const isHydrated = useIsHydrated();

  const dispatch = useDispatch();

  const pageData = useSelector((state) => state.page.pageData);
  const loading = useSelector((state) => state.page.isLoadingData);
  const nonce = useSelector((state) => state.nonce);

  const params = useParams();
  const pathname = params['*'] || '/';

  // Extract routing info from the host mapping.
  const hostname = pageData?.context?.content?.hostMapping?.hostname || '';
  const path = pageData?.context?.content?.hostMapping?.path || '';
  const isCustomDomain = pageData?.context?.content?.hostMapping?.isCustomDomain || false;

  // Keep the (SSR-initialised) data layer up to date on client-side navigation.
  // The title matches the one MetaData renders.
  const pageContent = pageData?.subject?.content;
  useDataLayerSync(
    pageContent?.ga?.dataLayer,
    pageContent?.meta?.title || pageContent?.title,
    loading,
  );

  useEffect(() => {
    // Skip for first render when page data is already available from SSR, but need to handle subsequent client-side navigation.
    if (!isHydrated) {
      return;
    }

    if (!hostname) {
      logger.warn(`No hostname was found in the SSR page data`, { hostname });
    }

    logger.info(`Fetching new page data for pathname`, { pathname });

    // Ensure page scrolls to top on navigation.
    window.scrollTo({ behavior: 'auto', top: 0 });

    // Get partner cookie value.
    const partner = getCookie(PartnerCookieName);

    dispatch(fetchPage(pathname, partner, hostname, path, isCustomDomain));

    // Purposefully only re-run this effect when pathname changes to avoid unnecessary page data fetches on other state or prop changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage, hostname, pathname]);

  return <Page loading={loading} nonce={nonce} pageData={pageData} />;
}

export default PageDataLoader;
